import {
  InformationOutline18Icon,
  LinkOutOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { ISirkModuleForShifts } from "features/CenterContent/RoleContent/ISirk/ISirkModuleForShifts";
import { ShiftSchedule } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ShiftSchedule";
import { VehicleReplacement } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/VehicleReplacement";
import {
  ActionsKebabMenu,
  InlineActions,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import { useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
} from "shared/components/Spor";
import {
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import { LANDING } from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { generatePath, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

const ShiftModule = styled(Module).attrs({ size: "lg" })`
  height: 100%;
  min-height: 0;

  && {
    border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const HorizontalScroll = styled.div`
  flex: 1;
  min-height: 0;
  margin-right: -15px;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 809px;
  display: flex;
  flex-direction: column;

  @container altTransportPage (width < 1696px) {
    min-width: 0;
  }
`;

const PanelScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 18px;
  padding-right: 15px;
`;

const ScrollModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(400px, 812px));
  gap: 12px;
  align-items: start;
  padding-bottom: 50px;

  @container altTransportPage (width < 1696px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colorBackgroundMain};
`;

interface AlternativeTransportRightPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportRightPanel = ({
  vehicle,
}: AlternativeTransportRightPanelProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = useDropsRole();

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  if (!vehicle.workShiftPlan) {
    return (
      <EmptyContainer>
        <Text fontWeight="400" fontSize="md">
          Ikke knyttet til skift
        </Text>
      </EmptyContainer>
    );
  }

  const { workShiftPlan, operatorName } = vehicle;
  const { workShiftTrips, shiftId, shiftDate, isirkId } = workShiftPlan;

  const timeInterval =
    workShiftTrips && workShiftTrips.length > 0
      ? `${workShiftTrips[0]?.scheduledDepartureTimeOrigin?.toLocaleTimeString(
          "no-NO",
          { hour: "2-digit", minute: "2-digit" },
        )}–${workShiftTrips[
          workShiftTrips.length - 1
        ]?.scheduledArrivalTimeDestination?.toLocaleTimeString("no-NO", {
          hour: "2-digit",
          minute: "2-digit",
        })}`
      : "";

  return (
    <ShiftModule
      title={
        <>
          <Box as="span" whiteSpace="nowrap">
            Skift:
          </Box>
          {shiftId ? (
            <>
              {" "}
              <Box as="span" whiteSpace="nowrap">
                {shiftId}
              </Box>
            </>
          ) : null}
        </>
      }
      action1={
        <>
          <ActionsKebabMenu>
            <VehicleReplacement
              vehicle={vehicle}
              distributionStopReferences={distributionStopReferences}
            />
          </ActionsKebabMenu>
          <InlineActions>
            <VehicleReplacement
              vehicle={vehicle}
              distributionStopReferences={distributionStopReferences}
            />
          </InlineActions>
        </>
      }
    >
      <Box
        flexShrink="0"
        pt="18px"
        pb="18px"
        mx="-18px"
        px="18px"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor={theme.colorOutlineSecondary}
      >
        {operatorName && workShiftTrips ? (
          <>
            <Text color={theme.colorTextSecondary}>{operatorName}</Text>
            <HStack>
              <Text>{timeInterval}</Text>
              <Text>({shiftDate?.toLocaleDateString("no-NO")})</Text>
            </HStack>
          </>
        ) : (
          <NoDataMessage reason="Det finnes ikke skiftinfo denne bussen" />
        )}
      </Box>
      <HorizontalScroll>
        <ScrollContent>
          <PanelScrollArea>
            <ScrollModuleGrid>
              <Module title="Skiftplan">
                <ShiftSchedule vehicle={vehicle} />
              </Module>

              <Module
                title="I-sirk-logg"
                badgeLeft1={
                  <Badge colorPalette="green" size="md">
                    {isirkId || "ukjent"}
                  </Badge>
                }
                size="md"
                action1={
                  <Button
                    size="xs"
                    fontSize="xs"
                    variant="tertiary"
                    onClick={() =>
                      navigate(
                        generatePath(LANDING, {
                          roleIdParam: role,
                          tabIdParam: "start",
                        }),
                      )
                    }
                  >
                    I-sirk-logg <LinkOutOutline18Icon />
                  </Button>
                }
                action2={
                  <Tooltip
                    style={{
                      width: "220px",
                      color: theme.colorBackgroundMain,
                      backgroundColor: theme.colorSuccessTextSecondary,
                      padding: "6px",
                      borderRadius: "12px",
                    }}
                    label="Denne loggen sendes til i-sirkloggen. Her vises kun det som er lagt inn på dette skiftet. Kundeveiledere har ikke tilgang til denne loggen."
                  >
                    <InformationOutline18Icon />
                  </Tooltip>
                }
              >
                <ISirkModuleForShifts
                  selectedDate={shiftDate?.toString() ?? ""}
                  shiftId={shiftId ?? ""}
                  operatorName={operatorName || ""}
                />
              </Module>
            </ScrollModuleGrid>
          </PanelScrollArea>
        </ScrollContent>
      </HorizontalScroll>
    </ShiftModule>
  );
};

export default AlternativeTransportRightPanel;
